/**
 * LavoriInOfficina.tsx — vista "Lavori in officina"
 *
 * Gli interventi attivi raggruppati per stato, per il capo officina che
 * controlla l'andamento anche da remoto.
 * Riferimento visivo: docs/lista-interventi-mockup-v3.html
 *
 * Tre gruppi che rispondono a tre domande diverse — cosa e' fermo, chi sta
 * lavorando, cosa aspetta — e solo il primo aperto: da telefono i blocchi si
 * vedono senza scorrere.
 *
 * Il tempo su ogni riga non e' un vezzo: "fermo da 4 giorni" e' cio' che
 * trasforma un elenco in un controllo.
 *
 * Dati: GET /work-orders/ (le auto in garage) + GET /work-orders/interventi-attivi
 * (i loro lavori). Chi e' in garage lo decide il server in un posto solo,
 * backend/app/services/presenza_officina.py, ed e' lo stesso criterio del Board
 * Giornaliero: le due schermate mostrano le stesse auto e lo stesso numero.
 */
import React, { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Collapse, Empty, Spin, Tag, Typography, theme } from 'antd'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import axiosInstance from '@/lib/axios'
import { AutoRefreshBadge } from '@/components/AutoRefreshBadge'

const { Text, Title } = Typography

// ── Il dato che arriva dall'endpoint ─────────────────────────────────────────
export interface InterventoAttivo {
  id: number
  progressivo: number | null
  descrizione_intervento: string
  tipo_intervento: string | null
  stato_codice: string | null
  stato_nome: string | null
  durata_stimata: number | null
  ore_effettive: number | null
  data_inizio: string | null
  data_sospensione: string | null
  sospensione_origine: string | null
  note_sospensione: string | null
  rda_pendente: { codice: string; fornitore: string | null } | null
  workshop_id: number | null
  workshop_nome: string | null
  work_order_id: number
  numero_scheda: string | null
  stato_scheda: string | null
  targa: string | null
  marca: string | null
  modello: string | null
  cliente: string | null
  data_entrata_veicolo: string | null
  data_fine_prevista: string | null
}

// Una scheda come arriva dalla lista /work-orders/: qui servono solo i campi
// per riconoscere l'auto, i lavori stanno nell'altra chiamata.
interface SchedaPresente {
  id: number
  numero_scheda: string | null
  stato: string | null
  // La lista schede serve i campi del veicolo e del cliente PIATTI, con il
  // prefisso: non sono oggetti annidati come nella risposta di dettaglio.
  vehicle_targa?: string | null
  vehicle_marca?: string | null
  vehicle_modello?: string | null
  customer_nome?: string | null
  data_entrata_veicolo?: string | null
}

// ── I tre gruppi ─────────────────────────────────────────────────────────────
type Gruppo = 'fermi' | 'corso' | 'avviare'

const GRUPPI: { chiave: Gruppo; titolo: string; statoCodice: string; pallino: string }[] = [
  { chiave: 'fermi',   titolo: 'Fermi',      statoCodice: 'sospeso',      pallino: 'warning' },
  { chiave: 'corso',   titolo: 'In corso',   statoCodice: 'iniziato',     pallino: 'primary' },
  { chiave: 'avviare', titolo: 'Da avviare', statoCodice: 'non_iniziato', pallino: 'neutro' },
]

// Le auto in garage che non hanno nessun lavoro aperto. Stanno qui e non
// altrove per una ragione precisa: questa pagina parte dagli INTERVENTI, e
// un'auto senza interventi non ha righe. Senza questo gruppo la pagina si
// fermerebbe alle auto con lavori e mostrerebbe un numero piu' basso di quello
// del Board, che parte dalle schede. Il nome e' quello che il Board usa gia'.
const TITOLO_SENZA_LAVORI = 'Da valutare'

// ── Formule di tempo ─────────────────────────────────────────────────────────
// Giorni interi trascorsi da una data. null se la data non c'e'.
function giorniDa(data: string | null): number | null {
  if (!data) return null
  return dayjs().startOf('day').diff(dayjs(data).startOf('day'), 'day')
}

function testoGiorni(g: number, prefisso: string): string {
  if (g <= 0) return `${prefisso} oggi`
  if (g === 1) return `${prefisso} da 1 giorno`
  return `${prefisso} da ${g} giorni`
}

// "scade oggi / domani / fra N giorni / scaduta da N giorni"
function testoScadenza(data: string | null): { testo: string; urgente: boolean } | null {
  if (!data) return null
  const g = dayjs(data).startOf('day').diff(dayjs().startOf('day'), 'day')
  if (g < 0) return { testo: `scaduta da ${-g} ${-g === 1 ? 'giorno' : 'giorni'}`, urgente: true }
  if (g === 0) return { testo: 'scade oggi', urgente: true }
  if (g === 1) return { testo: 'scade domani', urgente: true }
  return { testo: `scade fra ${g} giorni`, urgente: false }
}

const dataBreve = (d: string | null) => (d ? dayjs(d).format('DD/MM') : null)

// Ore effettive contro stimate. Con stima a 0 (o assente) si dice che manca,
// non si scrive "0 h stimate": sarebbe un dato inventato.
function testoOre(iv: InterventoAttivo): { testo: string; sfora: boolean } {
  const stimate = iv.durata_stimata ?? 0
  const effettive = iv.ore_effettive
  const num = (n: number) => String(n).replace('.', ',')
  if (!stimate) {
    return { testo: effettive ? `${num(effettive)} h · stima mancante` : 'stima mancante', sfora: false }
  }
  if (effettive == null) return { testo: `${num(stimate)} h stimate`, sfora: false }
  return { testo: `${num(effettive)} h su ${num(stimate)} stimate`, sfora: effettive > stimate }
}

// La causa del fermo: quella da RDA e' azionabile (si sollecita il fornitore),
// quella decisa da una persona no. Vanno distinte anche a colpo d'occhio.
function causaFermo(iv: InterventoAttivo): { testo: string; daRda: boolean } | null {
  if (iv.sospensione_origine === 'rda' && iv.rda_pendente) {
    const forn = iv.rda_pendente.fornitore ? ` · ${iv.rda_pendente.fornitore}` : ''
    return { testo: `In attesa di ricezione materiali — RDA ${iv.rda_pendente.codice}${forn}`, daRda: true }
  }
  const nota = iv.note_sospensione?.trim()

  if (iv.sospensione_origine === 'operatore') {
    return { testo: nota ? `Fermo dall'operatore: ${nota}` : "Fermo dall'operatore", daRda: false }
  }

  // Origine sconosciuta (le sospensioni scritte prima che la colonna esistesse):
  // non si puo' dire CHI ha fermato il lavoro, ma il motivo c'e' ed e' quello
  // che serve a chi controlla. Meglio mostrarlo che lasciare la riga muta.
  if (nota) return { testo: nota, daRda: false }

  return null
}

// ── Riga ─────────────────────────────────────────────────────────────────────

interface RigaProps {
  intervento: InterventoAttivo
  gruppo: Gruppo
  dentroGruppo: boolean
  onApri: (workOrderId: number) => void
}

const Riga: React.FC<RigaProps> = ({ intervento: iv, gruppo, dentroGruppo, onApri }) => {
  const { token } = theme.useToken()

  const coloreBordo =
    gruppo === 'fermi' ? token.colorWarning
    : gruppo === 'corso' ? token.colorPrimary
    : token.colorBorder

  const causa = gruppo === 'fermi' ? causaFermo(iv) : null
  const ore = gruppo === 'corso' ? testoOre(iv) : null
  const scadenza = gruppo === 'avviare' ? testoScadenza(iv.data_fine_prevista) : null
  const fermoDa = gruppo === 'fermi' ? giorniDa(iv.data_sospensione) : null
  const inCorsoDa = gruppo === 'corso' ? giorniDa(iv.data_inizio) : null

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onApri(iv.work_order_id)}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onApri(iv.work_order_id) } }}
      style={{
        background: token.colorBgContainer,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderLeft: `3px solid ${coloreBordo}`,
        borderRadius: dentroGruppo ? 0 : token.borderRadius,
        margin: dentroGruppo ? 0 : 8,
        borderTop: dentroGruppo ? 'none' : undefined,
        borderRight: dentroGruppo ? 'none' : undefined,
        borderBottom: dentroGruppo ? `1px solid ${token.colorBorderSecondary}` : undefined,
        padding: '12px 15px',
        cursor: 'pointer',
      }}
    >
      {/* Testata della riga: solo quando la riga sta da sola. Dentro un gruppo
          per scheda questi dati sono gia' nella testata del gruppo. */}
      {!dentroGruppo && (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <Text strong style={{ fontSize: 14, letterSpacing: '.02em' }}>{iv.targa || '—'}</Text>
          <Text style={{ fontSize: 13, color: token.colorTextSecondary }}>
            {[iv.marca, iv.modello].filter(Boolean).join(' ')}
          </Text>
          <Text style={{ fontSize: 13, color: token.colorTextSecondary }}>{iv.cliente}</Text>
          <Text style={{ fontSize: 12, color: token.colorTextTertiary, marginLeft: 'auto', whiteSpace: 'nowrap' }}>
            scheda {iv.numero_scheda}
          </Text>
        </div>
      )}

      <div style={{ fontSize: 14, marginTop: 5, color: token.colorText }}>
        {dentroGruppo && iv.progressivo != null && (
          <Text style={{ color: token.colorTextTertiary }}>{iv.progressivo} · </Text>
        )}
        {iv.descrizione_intervento}
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 7, fontSize: 12 }}>
        <Tag
          color={gruppo === 'fermi' ? 'warning' : gruppo === 'corso' ? 'processing' : 'default'}
          style={{ margin: 0, fontWeight: 600 }}
        >
          {iv.stato_nome || iv.stato_codice || '—'}
        </Tag>

        {!dentroGruppo && iv.data_entrata_veicolo && (
          <Text style={{ fontSize: 12, color: token.colorTextTertiary, whiteSpace: 'nowrap' }}>
            in officina dal {dataBreve(iv.data_entrata_veicolo)}
          </Text>
        )}

        {fermoDa != null && (
          <Tag color={fermoDa >= 7 ? 'warning' : 'default'} style={{ margin: 0 }}>
            {testoGiorni(fermoDa, 'fermo')}
          </Tag>
        )}
        {inCorsoDa != null && (
          <Tag color={inCorsoDa >= 7 ? 'warning' : 'default'} style={{ margin: 0 }}>
            {testoGiorni(inCorsoDa, 'in corso')}
          </Tag>
        )}
        {ore && (
          <Tag color={ore.sfora ? 'error' : 'default'} style={{ margin: 0 }}>{ore.testo}</Tag>
        )}
        {scadenza && (
          <Tag color={scadenza.urgente ? 'error' : 'default'} style={{ margin: 0 }}>{scadenza.testo}</Tag>
        )}

        {iv.tipo_intervento && <Tag color="blue" style={{ margin: 0 }}>{iv.tipo_intervento}</Tag>}
        {iv.workshop_nome && <Tag color="purple" style={{ margin: 0 }}>{iv.workshop_nome}</Tag>}
      </div>

      {causa && (
        <div
          style={{
            marginTop: 7,
            fontSize: 13,
            padding: '7px 10px',
            borderRadius: token.borderRadiusSM,
            background: causa.daRda ? token.colorWarningBg : token.colorFillQuaternary,
            border: `1px solid ${causa.daRda ? token.colorWarningBorder : token.colorBorderSecondary}`,
            color: causa.daRda ? token.colorWarningText : token.colorTextSecondary,
          }}
        >
          {causa.testo}
        </div>
      )}
    </div>
  )
}

// ── Corpo di un gruppo ───────────────────────────────────────────────────────
// Il raggruppamento per scheda compare SOLO dove la scheda ha piu' di un
// intervento in questo gruppo: con un intervento solo sarebbe un'intestazione
// che non dice niente (164 schede su 184 hanno un intervento solo).

const CorpoGruppo: React.FC<{ interventi: InterventoAttivo[]; gruppo: Gruppo; onApri: (id: number) => void }> = ({
  interventi, gruppo, onApri,
}) => {
  const { token } = theme.useToken()

  const perScheda = useMemo(() => {
    const mappa = new Map<number, InterventoAttivo[]>()
    for (const iv of interventi) {
      const lista = mappa.get(iv.work_order_id) ?? []
      lista.push(iv)
      mappa.set(iv.work_order_id, lista)
    }
    return [...mappa.values()]
  }, [interventi])

  if (interventi.length === 0) {
    return (
      <div style={{ padding: '20px 15px', textAlign: 'center' }}>
        <Text type="secondary" style={{ fontSize: 13 }}>Nessun intervento in questo gruppo</Text>
      </div>
    )
  }

  return (
    <div style={{ background: token.colorFillQuaternary, paddingTop: 1, paddingBottom: 1 }}>
      {perScheda.map(gruppoScheda => {
        const primo = gruppoScheda[0]

        if (gruppoScheda.length === 1) {
          return (
            <Riga key={primo.id} intervento={primo} gruppo={gruppo} dentroGruppo={false} onApri={onApri} />
          )
        }

        return (
          <div
            key={primo.work_order_id}
            style={{
              margin: 8,
              border: `1px solid ${token.colorBorder}`,
              borderRadius: token.borderRadius,
              background: token.colorBgContainer,
              overflow: 'hidden',
            }}
          >
            <div style={{
              padding: '9px 13px',
              background: token.colorFillQuaternary,
              borderBottom: `1px solid ${token.colorBorderSecondary}`,
              display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', fontSize: 13,
            }}>
              <Text strong style={{ fontSize: 14, letterSpacing: '.02em' }}>{primo.targa || '—'}</Text>
              <Text style={{ color: token.colorTextSecondary }}>
                {[primo.marca, primo.modello].filter(Boolean).join(' ')}
              </Text>
              <Text style={{ color: token.colorTextSecondary }}>{primo.cliente}</Text>
              <Text style={{ color: token.colorTextTertiary, marginLeft: 'auto', whiteSpace: 'nowrap' }}>
                scheda {primo.numero_scheda} · {gruppoScheda.length} interventi
              </Text>
            </div>

            {(primo.data_entrata_veicolo || primo.data_fine_prevista) && (
              <div style={{
                display: 'flex', gap: 14, flexWrap: 'wrap', padding: '0 13px 9px',
                background: token.colorFillQuaternary,
                borderBottom: `1px solid ${token.colorBorderSecondary}`,
              }}>
                {primo.data_entrata_veicolo && (
                  <Text style={{ fontSize: 12, color: token.colorTextTertiary }}>
                    in officina dal {dataBreve(primo.data_entrata_veicolo)}
                  </Text>
                )}
                {primo.data_fine_prevista && (
                  <Text style={{ fontSize: 12, color: token.colorTextSecondary, fontWeight: 500 }}>
                    consegna prevista {dataBreve(primo.data_fine_prevista)}
                  </Text>
                )}
              </div>
            )}

            {gruppoScheda.map(iv => (
              <Riga key={iv.id} intervento={iv} gruppo={gruppo} dentroGruppo onApri={onApri} />
            ))}
          </div>
        )
      })}
    </div>
  )
}

// ── Pagina ───────────────────────────────────────────────────────────────────

const LavoriInOfficina: React.FC = () => {
  const { token } = theme.useToken()
  const navigate = useNavigate()

  // Le auto in garage oggi. Stessa chiamata e stesso criterio del Board
  // Giornaliero: `entrata_a` fa applicare al server presenza_officina.
  const oggi = dayjs().format('YYYY-MM-DD')
  const { data: schedeData, isLoading: loadingSchede, error: erroreSchede, dataUpdatedAt } = useQuery({
    queryKey: ['officina-schede-presenti', oggi],
    queryFn: async () => {
      const params = new URLSearchParams({
        skip: '0', limit: '500', order_by: 'numero_scheda', order_dir: 'asc', entrata_a: oggi,
      })
      const res = await axiosInstance.get<{ items: SchedaPresente[] }>(`/work-orders/?${params.toString()}`)
      return res.data
    },
    retry: 1,
    staleTime: 0,
    refetchInterval: 60_000,
  })

  // I lavori di quelle auto. `stati=in-officina` toglie il restringimento per
  // stato scheda: le auto le ha gia' scelte il criterio di presenza, e se qui
  // restringessimo ancora le due schermate tornerebbero a divergere.
  const { data, isLoading, error } = useQuery({
    queryKey: ['interventi-attivi', 'in-officina'],
    queryFn: async () => {
      const res = await axiosInstance.get<{ items: InterventoAttivo[]; total: number }>(
        '/work-orders/interventi-attivi?stati=in-officina'
      )
      return res.data
    },
    retry: 1,
    staleTime: 0,
    refetchInterval: 60_000,
  })

  const perGruppo = useMemo(() => {
    const items = data?.items ?? []
    const mappa: Record<Gruppo, InterventoAttivo[]> = { fermi: [], corso: [], avviare: [] }
    for (const g of GRUPPI) {
      mappa[g.chiave] = items.filter(i => i.stato_codice === g.statoCodice)
    }
    // "Da avviare" e' il gruppo piu' numeroso: senza un criterio e' un muro.
    // La scadenza della scheda e' quello che conta; le schede senza data in fondo.
    mappa.avviare = [...mappa.avviare].sort((a, b) => {
      if (!a.data_fine_prevista && !b.data_fine_prevista) return 0
      if (!a.data_fine_prevista) return 1
      if (!b.data_fine_prevista) return -1
      return dayjs(a.data_fine_prevista).valueOf() - dayjs(b.data_fine_prevista).valueOf()
    })
    return mappa
  }, [data])

  // Le auto presenti che non compaiono in NESSUN gruppo. Il criterio e' "non ha
  // lavori aperti", non "non ha interventi": un'auto con tutti i lavori conclusi
  // e' ancora in garage e non sta in nessuno dei tre gruppi, che raccolgono solo
  // sospeso, iniziato e non iniziato. Prima spariva dalla pagina, e il totale in
  // testa non tornava con le righe.
  const senzaLavori = useMemo(() => {
    const conLavoriAperti = new Set(
      GRUPPI.flatMap(g => perGruppo[g.chiave].map(i => i.work_order_id))
    )
    return (schedeData?.items ?? []).filter(w => !conLavoriAperti.has(w.id))
  }, [perGruppo, schedeData])

  // Il numero in testa conta le AUTO, non i lavori: e' quello che deve coincidere
  // con il Board. I tre gruppi contano LAVORI, e la loro somma puo' superare le
  // auto, perche' una scheda con lavori in stati diversi compare in piu' gruppi.
  // Per questo la riga dice cosa conta ogni numero, invece di lasciarli sommare.
  const veicoli = schedeData?.items?.length ?? 0
  const lavoriAperti = GRUPPI.reduce((tot, g) => tot + perGruppo[g.chiave].length, 0)

  const apriScheda = (workOrderId: number) => navigate(`/schede-lavoro/${workOrderId}`)

  if (isLoading || loadingSchede) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
        <Spin size="large" />
      </div>
    )
  }

  if (error || erroreSchede) {
    return (
      <Alert
        type="error"
        showIcon
        message="Impossibile caricare i lavori in officina"
        description={((error || erroreSchede) as any)?.response?.data?.detail || 'Riprova fra poco.'}
      />
    )
  }

  return (
    <div style={{ paddingBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <Title level={4} style={{ margin: 0 }}>Lavori in officina</Title>
        <AutoRefreshBadge updatedAt={dataUpdatedAt || null} />
      </div>

      <div style={{ margin: '4px 0 16px', fontSize: 13, color: token.colorTextSecondary }}>
        <strong>{veicoli}</strong> {veicoli === 1 ? 'veicolo' : 'veicoli'} in officina
        {' — '}{lavoriAperti} {lavoriAperti === 1 ? 'lavoro aperto' : 'lavori aperti'}
        {' ('}{perGruppo.fermi.length} fermi, {perGruppo.corso.length} in corso,
        {' '}{perGruppo.avviare.length} da avviare{')'}
        {senzaLavori.length > 0 && <>{' e '}{senzaLavori.length} {senzaLavori.length === 1 ? 'auto' : 'auto'} da valutare</>}
      </div>

      {veicoli === 0 ? (
        <Empty description="Nessun veicolo in officina" />
      ) : (
        <Collapse
          // Solo il primo aperto: chi guarda dal telefono vede i blocchi senza scorrere.
          defaultActiveKey={['fermi']}
          items={GRUPPI.map(g => ({
            key: g.chiave as string,
            label: (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{
                  width: 9, height: 9, borderRadius: '50%', flexShrink: 0,
                  background: g.pallino === 'warning' ? token.colorWarning
                    : g.pallino === 'primary' ? token.colorPrimary
                    : token.colorBorder,
                }} />
                <span style={{ fontSize: 15, fontWeight: 600, flex: 1 }}>{g.titolo}</span>
                <span style={{ fontSize: 13, color: token.colorTextSecondary, fontVariantNumeric: 'tabular-nums' }}>
                  {perGruppo[g.chiave].length}
                </span>
              </div>
            ),
            children: (
              <CorpoGruppo interventi={perGruppo[g.chiave]} gruppo={g.chiave} onApri={apriScheda} />
            ),
            styles: { body: { padding: 0 } },
          })).concat(senzaLavori.length === 0 ? [] : [{
            key: 'senza-lavori',
            label: (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{
                  width: 9, height: 9, borderRadius: '50%', flexShrink: 0,
                  background: token.colorWarning,
                }} />
                <span style={{ fontSize: 15, fontWeight: 600, flex: 1 }}>{TITOLO_SENZA_LAVORI}</span>
                <span style={{ fontSize: 13, color: token.colorTextSecondary, fontVariantNumeric: 'tabular-nums' }}>
                  {senzaLavori.length}
                </span>
              </div>
            ),
            children: (
              <div>
                {senzaLavori.map(w => {
                  const cliente = w.customer_nome || ''
                  return (
                    <div
                      key={w.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => apriScheda(w.id)}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); apriScheda(w.id) } }}
                      style={{
                        background: token.colorBgContainer,
                        borderLeft: `3px solid ${token.colorWarning}`,
                        borderBottom: `1px solid ${token.colorBorderSecondary}`,
                        padding: '12px 15px',
                        cursor: 'pointer',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                        <Text strong style={{ fontSize: 14, letterSpacing: '.02em' }}>{w.vehicle_targa || '—'}</Text>
                        <Text style={{ fontSize: 13, color: token.colorTextSecondary }}>
                          {[w.vehicle_marca, w.vehicle_modello].filter(Boolean).join(' ')}
                        </Text>
                        <Text style={{ fontSize: 13, color: token.colorTextSecondary }}>{cliente}</Text>
                        <Text style={{ fontSize: 12, color: token.colorTextTertiary, marginLeft: 'auto', whiteSpace: 'nowrap' }}>
                          scheda {w.numero_scheda}
                        </Text>
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 7, fontSize: 12 }}>
                        <Tag color="default" style={{ margin: 0, fontWeight: 600 }}>{w.stato || '—'}</Tag>
                        <Text style={{ fontSize: 12, color: token.colorTextTertiary }}>nessun lavoro aperto</Text>
                        {w.data_entrata_veicolo && (
                          <Text style={{ fontSize: 12, color: token.colorTextTertiary, whiteSpace: 'nowrap' }}>
                            in officina dal {dataBreve(w.data_entrata_veicolo)}
                          </Text>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            ),
            styles: { body: { padding: 0 } },
          }])}
        />
      )}
    </div>
  )
}

export default LavoriInOfficina
